var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { defineComponent as _defineComponent } from 'vue';
import { library } from "@fortawesome/fontawesome-svg-core";
import { faCaretDown, faCaretUp, faCopy, faEdit, faUserPlus, faUserSlash } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { BFormCheckbox } from "bootstrap-vue";
import { computed, nextTick, reactive, ref, watch } from "vue";
import { getGalaxyInstance } from "@/app";
import { useToast } from "@/composables/toast";
import { getAppRoot } from "@/onload/loadConfig";
import { errorMessageAsString } from "@/utils/simple-error";
import { getFullAppUrl } from "@/utils/utils";
import EditableUrl from "./EditableUrl.vue";
import WorkflowEmbed from "./Embeds/WorkflowEmbed.vue";
import ErrorMessages from "./ErrorMessages.vue";
import UserSharing from "./UserSharing.vue";
import Heading from "@/components/Common/Heading.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'SharingPage',
    props: {
        id: null,
        pluralName: null,
        modelClass: null
    },
    setup(__props) {
        const props = __props;
        library.add(faCopy, faEdit, faUserPlus, faUserSlash, faCaretDown, faCaretUp);
        const errors = ref([]);
        function addError(newError) {
            // temporary turning Set into Array, until we update till Vue 3.0, that supports Set reactivity
            errors.value = Array.from(new Set(errors.value).add(newError));
        }
        function onErrorDismissed(index) {
            errors.value.splice(index, 1);
        }
        const defaultExtra = () => ({
            can_change: [],
            cannot_change: [],
        });
        const item = ref({
            title: "title",
            username_and_slug: "__username__/__slug__",
            importable: false,
            published: false,
            users_shared_with: [],
            extra: defaultExtra(),
        });
        const itemUrl = reactive({
            prefix: "",
            slug: "",
        });
        watch(() => item.value.username_and_slug, (value) => {
            if (value) {
                const index = value.lastIndexOf("/");
                itemUrl.prefix = getFullAppUrl(value.substring(0, index + 1));
                itemUrl.slug = value.substring(index + 1);
            }
        }, { immediate: true });
        const slugUrl = computed(() => `${getAppRoot()}api/${props.pluralName.toLowerCase()}/${props.id}/slug`);
        function onChangeSlug(newValue) {
            itemUrl.slug = newValue;
        }
        function onSubmitSlug(newValue) {
            return __awaiter(this, void 0, void 0, function* () {
                itemUrl.slug = newValue;
                try {
                    yield axios.put(slugUrl.value, { new_slug: newValue });
                }
                catch (e) {
                    onError(e);
                }
            });
        }
        const itemStatus = computed(() => (item.value.published ? "accessible via link and published" : "accessible via link"));
        const publishedUrl = computed(() => `${getAppRoot()}${props.pluralName.toLocaleLowerCase()}/list_published`);
        function onError(axiosError) {
            addError(errorMessageAsString(axiosError));
        }
        const ready = ref(false);
        function getSharing() {
            return __awaiter(this, void 0, void 0, function* () {
                ready.value = false;
                try {
                    const response = yield axios.get(`${getAppRoot()}api/${props.pluralName.toLocaleLowerCase()}/${props.id}/sharing`);
                    assignItem(response.data, true);
                }
                catch (e) {
                    onError(e);
                }
            });
        }
        getSharing();
        function permissionsChangeRequired(data) {
            if (data.extra) {
                return data.extra.can_change.length > 0 || data.extra.cannot_change.length > 0;
            }
            else {
                return false;
            }
        }
        const actions = {
            enableLinkAccess: "enable_link_access",
            disableLinkAccess: "disable_link_access",
            publish: "publish",
            unpublish: "unpublish",
            share_with: "share_with_users",
        };
        const { success } = useToast();
        function setSharing(action, userId, shareOption) {
            return __awaiter(this, void 0, void 0, function* () {
                let userIds;
                if (Array.isArray(userId)) {
                    userIds = userId;
                }
                else {
                    userIds = userId ? userId.replace(/ /g, "").split(",") : undefined;
                }
                const data = {
                    user_ids: userIds,
                    share_option: shareOption ? shareOption : undefined,
                };
                try {
                    const response = yield axios.put(`${getAppRoot()}api/${props.pluralName.toLocaleLowerCase()}/${props.id}/${action}`, data);
                    errors.value = [];
                    const userIdsSaved = userIds && !permissionsChangeRequired(response.data) && response.data.errors.length === 0;
                    assignItem(response.data, userIdsSaved !== null && userIdsSaved !== void 0 ? userIdsSaved : false);
                    if (!permissionsChangeRequired(response.data)) {
                        success("Sharing preferences saved");
                    }
                }
                catch (e) {
                    onError(e);
                }
            });
        }
        const userSharing = ref();
        function assignItem(newItem, overwriteCandidates) {
            var _a, _b, _c;
            return __awaiter(this, void 0, void 0, function* () {
                if (newItem.errors) {
                    errors.value = newItem.errors;
                }
                item.value = newItem;
                if ((_b = (!item.value.extra || ((_a = newItem.errors) === null || _a === void 0 ? void 0 : _a.length))) !== null && _b !== void 0 ? _b : 0 > 0) {
                    item.value.extra = defaultExtra();
                }
                if (overwriteCandidates) {
                    yield nextTick();
                    //@ts-ignore incorrect property not found error
                    (_c = userSharing.value) === null || _c === void 0 ? void 0 : _c.reset();
                }
                ready.value = true;
            });
        }
        function onImportable(importable) {
            if (importable) {
                setSharing(actions.enableLinkAccess);
            }
            else {
                item.value.published = false;
                setSharing(actions.disableLinkAccess);
            }
        }
        function onPublish(published) {
            if (published) {
                item.value.importable = true;
                setSharing(actions.publish);
            }
            else {
                setSharing(actions.unpublish);
            }
        }
        const hasUsername = ref(Boolean(getGalaxyInstance().user.get("username")));
        const newUsername = ref("");
        const slugSet = computed(() => itemUrl.slug != "__slug__" && itemUrl.prefix != "__username__");
        function setUsername() {
            return __awaiter(this, void 0, void 0, function* () {
                axios
                    .put(`${getAppRoot()}api/users/${getGalaxyInstance().user.id}/information/inputs`, {
                    username: newUsername.value || "",
                })
                    .then((response) => {
                    hasUsername.value = true;
                    getSharing();
                })
                    .catch(onError);
            });
        }
        const embedable = computed(() => item.value.importable && props.modelClass.toLocaleLowerCase() === "workflow");
        return { __sfc: true, props, errors, addError, onErrorDismissed, defaultExtra, item, itemUrl, slugUrl, onChangeSlug, onSubmitSlug, itemStatus, publishedUrl, onError, ready, getSharing, permissionsChangeRequired, actions, success, setSharing, userSharing, assignItem, onImportable, onPublish, hasUsername, newUsername, slugSet, setUsername, embedable, BFormCheckbox, EditableUrl, WorkflowEmbed, ErrorMessages, UserSharing, Heading };
    }
});
